/**
 * Syncs files between bot materials and OpenAI vector store
 *
 * @param {Object} params
 * @param {Object} params.botData - Original bot data (before save)
 * @param {Object} params.updatedBotData - Updated bot data being saved
 * @param {Function} params.createVectorStoreFn - Async function to create vector store
 * @param {Function} params.addFileFn - Async function to add file to vector store
 * @param {Function} params.removeFileFn - Async function to remove file from vector store
 * @param {Function} params.refetchFilesFn - Async function to fetch current files in vector store
 * @returns {Promise<string|null>} Vector store ID or null if no materials
 */
export async function syncVectorStoreFiles({
    botData,
    updatedBotData,
    createVectorStoreFn,
    addFileFn,
    removeFileFn,
    refetchFilesFn
}) {
    // Ensure materials array exists and filter out legacy materials (those without id)
    const validMaterials = (updatedBotData.config.materials || []).filter(m => m.id && !m.isLegacy);

    // If no valid materials, don't create/manage vector store
    if (validMaterials.length === 0) {
        // If there was a vector store but no materials now, we could delete it
        // For now, just return null to indicate no vector store needed
        return null;
    }

    let currentVectorStoreId = updatedBotData.vector_store_id;

    // 1. Create vector store if it doesn't exist
    if (!currentVectorStoreId) {
        try {
            const createRes = await createVectorStoreFn({});
            // OpenAI returns the vector store object directly in data
            currentVectorStoreId = createRes?.id;
            if (!currentVectorStoreId) {
                console.error("Vector store creation failed - no ID returned:", createRes);
                throw new Error("Vector store creation failed - no ID in response");
            }
            updatedBotData.vector_store_id = currentVectorStoreId;
        } catch (error) {
            console.error("Error creating vector store:", error.response ? error.response.data : error);
            throw error;
        }
    }

    // 2. Sync files to the vector store
    try {
        let existingFileIds = new Set();

        // Only fetch existing files if there was ALREADY a vector store before this save
        if (botData?.vector_store_id && botData.vector_store_id === currentVectorStoreId && refetchFilesFn) {
            // This is an existing vector store, fetch its current files
            try {
                const { data: currentVectorStoreFiles } = await refetchFilesFn();
                existingFileIds = new Set(currentVectorStoreFiles?.data?.map(f => f.id) || []);
            } catch (error) {
                console.warn("Failed to fetch existing vector store files, assuming empty:", error);
                // Continue with empty set - new files will be added
            }
        }
        // If we just created the vector store, existingFileIds remains empty (new vector store has no files)

        const botMaterialFileIds = new Set(validMaterials.map(m => m.id));

        // Add new files from bot materials to vector store
        for (const material of validMaterials) {
            if (!existingFileIds.has(material.id)) {
                console.log(`Adding file ${material.id} to vector store ${currentVectorStoreId}`);
                await addFileFn({ vectorStoreId: currentVectorStoreId, fileId: material.id });
            }
        }

        // Remove files from vector store that are no longer in bot materials
        for (const existingFileId of existingFileIds) {
            if (!botMaterialFileIds.has(existingFileId)) {
                console.log(`Removing file ${existingFileId} from vector store ${currentVectorStoreId}`);
                await removeFileFn({ vectorStoreId: currentVectorStoreId, fileId: existingFileId });
            }
        }
    } catch (error) {
        console.error("Error syncing files with vector store:", error);
        throw error;
    }

    return currentVectorStoreId;
}

/**
 * Ensures file_search tool is added/removed based on materials presence
 *
 * @param {Array} tools - Current assistant tools array
 * @param {Array} materials - Bot materials array
 * @returns {Array} Updated tools array
 */
export function ensureFileSearchTool(tools = [], materials = []) {
    // Filter valid materials (those with id and not legacy)
    const validMaterials = (materials || []).filter(m => m.id && !m.isLegacy);

    // Make a copy of tools to avoid mutations
    let updatedTools = [...tools];

    if (validMaterials.length > 0) {
        // Add file_search tool if not present
        const hasFileSearchTool = updatedTools.some(tool => tool.type === "file_search");
        if (!hasFileSearchTool) {
            updatedTools.push({ type: "file_search" });
        }
    } else {
        // Remove file_search tool if no materials
        updatedTools = updatedTools.filter(tool => tool.type !== "file_search");
    }

    return updatedTools;
}
